import { useCallback, useEffect, useRef, useState } from "react";

import { quickbooksService } from "../../../../../api/quickbooks";

export default function useQuickbooksSyncActions({
  notify,
  refresh,
  selectedRows,
  setSelectedRows,
  onSortChange,
}) {
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncModalContext, setSyncModalContext] = useState({
    customer: null,
    customerIds: [],
    selectedCount: 0,
  });
  const [syncLoading, setSyncLoading] = useState(false);
  const [quickbooksSyncingCustomerIds, setQuickbooksSyncingCustomerIds] =
    useState(new Set());

  const pollTimeoutRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);
  const pollTokenRef = useRef(0);
  const isMountedRef = useRef(true);

  const clearTimers = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  }, []);

  const cancelPolling = useCallback(() => {
    pollTokenRef.current += 1;
    clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      cancelPolling();
    };
  }, [cancelPolling]);

  const openSingleSyncModal = useCallback(
    (customer) => {
      if (!customer?.id) {
        notify?.({
          open: true,
          message: "Unable to identify customer for syncing.",
          severity: "error",
        });
        return;
      }

      setSyncModalContext({
        customer,
        customerIds: [customer.id],
        selectedCount: 1,
      });
      setSyncModalOpen(true);
    },
    [notify]
  );

  const openBulkSyncModal = useCallback(() => {
    if (!selectedRows?.length) {
      notify?.({
        open: true,
        message: "Select at least one account to start a bulk sync.",
        severity: "info",
      });
      return;
    }

    setSyncModalContext({
      customer: null,
      customerIds: selectedRows,
      selectedCount: selectedRows.length,
    });
    setSyncModalOpen(true);
  }, [notify, selectedRows]);

  const closeSyncModal = useCallback(() => {
    if (syncLoading) return;
    setSyncModalOpen(false);
    cancelPolling();
  }, [cancelPolling, syncLoading]);

  const handleSyncConfirmed = useCallback(
    async (syncPayload) => {
      // starting a new sync should cancel any in-flight polling from a prior sync
      cancelPolling();

      const { customerIds = [], customerId: singleCustomerId, ...rest } =
        syncPayload || {};

      const normalizedIds = Array.isArray(customerIds)
        ? customerIds.filter(Boolean)
        : [];

      const targetIds =
        normalizedIds.length > 0
          ? normalizedIds
          : singleCustomerId
          ? [singleCustomerId]
          : [];

      if (!targetIds.length) return;

      const isBulk = targetIds.length > 1;
      const payload = {
        ...rest,
        ...(isBulk
          ? { customer_ids: targetIds }
          : { customerId: targetIds[0] }),
      };

      setSyncLoading(true);

      try {
        const response = await quickbooksService.syncQuickbooksData(payload);

        if (response.data?.completed_sync) {
          const customerName =
            syncModalContext.customer?.name ||
            (isBulk ? `${targetIds.length} customers` : "customer");

          // CDC sync can return completed_sync:true AND an errors array when it
          // finishes with partial failures — treat that as an error, not success.
          const completedErrors = response.data?.errors;
          const hasCompletedErrors =
            Array.isArray(completedErrors) && completedErrors.length > 0;

          if (hasCompletedErrors) {
            const failureMessage = completedErrors
              .map((e) =>
                typeof e === "string" ? e : e?.message || JSON.stringify(e)
              )
              .join("\n");
            notify?.({
              open: true,
              message: `QuickBooks sync completed with errors for ${customerName}: ${failureMessage}`,
              severity: "error",
            });
          } else {
            notify?.({
              open: true,
              message: `QuickBooks sync completed for ${customerName}.`,
              severity: "success",
            });

            if (isBulk) {
              setSelectedRows?.([]);
            }
          }

          setSyncModalOpen(false);
          await refresh?.();
          return;
        }

        setQuickbooksSyncingCustomerIds((prev) => {
          const updated = new Set(prev);
          targetIds.forEach((id) => updated.add(id));
          return updated;
        });

        notify?.({
          open: true,
          message: `QuickBooks sync started for ${
            syncModalContext.customer?.name ||
            (isBulk ? `${targetIds.length} customers` : "customer")
          }.`,
          severity: "success",
        });

        setSyncModalOpen(false);
        await refresh?.();

        const jobId = response.data?.job_id;

        if (jobId) {
          const pollToken = pollTokenRef.current;
          const POLL_INTERVAL_MS = 3000;
          const INITIAL_POLL_DELAY_MS = 2000;
          const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes
          const pollStartMs = Date.now();

          const safeClearSyncingIds = () => {
            if (!isMountedRef.current) return;
            setQuickbooksSyncingCustomerIds((prev) => {
              const updated = new Set(prev);
              targetIds.forEach((id) => updated.delete(id));
              return updated;
            });
          };

          const pollJobStatus = async () => {
            try {
              const jobResponse = await quickbooksService.getJobDetails(jobId);
              const jobData = jobResponse.data;

              if (
                jobData?.status === "completed" ||
                jobData?.status === "failed"
              ) {
                safeClearSyncingIds();

                if (jobData.status === "completed") {
                  notify?.({
                    open: true,
                    message: `QuickBooks sync completed for ${
                      syncModalContext.customer?.name ||
                      (isBulk ? `${targetIds.length} customers` : "customer")
                    }.`,
                    severity: "success",
                  });

                  if (isBulk) {
                    setSelectedRows?.([]);
                  }
                } else {
                  // Errors are nested in results[customerId][activityType].errors
                  const resultsMap = jobData?.results || {};
                  const errorLines = [];
                  for (const customerResults of Object.values(resultsMap)) {
                    if (
                      !customerResults ||
                      typeof customerResults !== "object"
                    )
                      continue;
                    for (const activityDetail of Object.values(customerResults)) {
                      if (
                        !activityDetail ||
                        typeof activityDetail !== "object"
                      )
                        continue;
                      const actErrors = activityDetail.errors;
                      if (Array.isArray(actErrors) && actErrors.length > 0) {
                        actErrors.forEach((e) =>
                          errorLines.push(
                            typeof e === "string"
                              ? e
                              : e?.message || JSON.stringify(e)
                          )
                        );
                      }
                    }
                  }
                  const failureMessage =
                    errorLines.length > 0
                      ? errorLines.join("\n")
                      : jobData?.error || "The sync job encountered an error.";

                  notify?.({
                    open: true,
                    message: `QuickBooks sync failed: ${failureMessage}`,
                    severity: "error",
                  });
                }

                await refresh?.();
                return true; // stop polling
              }

              return false; // continue polling
            } catch (pollError) {
              if (pollError?.response?.status === 404) {
                // Job no longer exists — treat as done
                safeClearSyncingIds();
                await refresh?.();
                return true;
              }
              return false; // transient error, keep polling
            }
          };

          const poll = async () => {
            // canceled due to unmount/new sync/close
            if (pollToken !== pollTokenRef.current) return;

            const elapsedMs = Date.now() - pollStartMs;
            if (elapsedMs >= MAX_POLL_DURATION_MS) {
              safeClearSyncingIds();
              notify?.({
                open: true,
                message:
                  "QuickBooks sync is taking longer than expected and has timed out. Please try again.",
                severity: "error",
              });
              await refresh?.();
              return;
            }

            const shouldStop = await pollJobStatus();

            // canceled while awaiting network
            if (pollToken !== pollTokenRef.current) return;

            if (!shouldStop) {
              pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            }
          };

          pollTimeoutRef.current = setTimeout(poll, INITIAL_POLL_DELAY_MS);
        } else {
          // No job_id returned — fall back to clearing after 5s
          fallbackTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            setQuickbooksSyncingCustomerIds((prev) => {
              const updated = new Set(prev);
              targetIds.forEach((id) => updated.delete(id));
              return updated;
            });
          }, 5000);
        }
      } catch (error) {
        // Backend returns errors as either { error: "..." } (singular string) or
        // { errors: ["...", ...] } (plural array) depending on the code path.
        const responseData = error?.response?.data;
        const failureMessage =
          responseData?.error ||
          (Array.isArray(responseData?.errors) && responseData.errors.length > 0
            ? responseData.errors.join(" ")
            : null) ||
          "Failed to start QuickBooks sync. Please try again.";

        notify?.({
          open: true,
          message: failureMessage,
          severity: "error",
        });
      } finally {
        setSyncLoading(false);
      }
    },
    [cancelPolling, notify, refresh, setSelectedRows, syncModalContext.customer]
  );

  const handleExportSelected = useCallback(async () => {
    const selectedCount = selectedRows?.length || 0;

    try {
      const response = await quickbooksService.exportOverview(
        selectedCount > 0 ? selectedRows : null
      );

      const link = document.createElement("a");
      link.href = response.data.file;
      link.setAttribute("download", response.data.name);
      document.body.appendChild(link);
      link.click();
      link.remove();

      notify?.({
        open: true,
        message:
          selectedCount > 0
            ? `${selectedCount} selected accounts exported successfully!`
            : "All accounts exported successfully!",
        severity: "success",
      });

      setSelectedRows?.([]);
      onSortChange?.(null, null);
    } catch {
      notify?.({
        open: true,
        message: "Failed to export accounts. Please try again.",
        severity: "error",
      });
    }
  }, [notify, onSortChange, selectedRows, setSelectedRows]);

  return {
    syncModalOpen,
    syncModalContext,
    syncLoading,
    quickbooksSyncingCustomerIds,
    openSingleSyncModal,
    openBulkSyncModal,
    closeSyncModal,
    handleSyncConfirmed,
    handleExportSelected,
  };
}